import { Link } from "react-router-dom";
import { ArrowRight } from "@phosphor-icons/react";

const STEPS = [
  {
    code: "01",
    title: "Intake & Scope",
    body: "Submit your project brief and documents via the contact or free-trial form. Within hours we confirm scope, turnaround, pricing, and flag any missing inputs from our documents checklist.",
    bullets: [
      "Review field notes, point files, deeds, and title work",
      "Confirm jurisdiction standards and required Table A items",
      "Lock in CAD template, layering, and datum",
    ],
  },
  {
    code: "02",
    title: "CAD Drafting",
    body: "Your dedicated drafter produces the deliverable in AutoCAD, Civil 3D, or your preferred platform — matching your template exactly. We run internal closure and layer audits as we draft.",
    bullets: [
      "Match your template, title block, and layer standards",
      "Process raw data (.rw5, .tds) and point clouds (.las)",
      "Annotate bearings, distances, curves, easements",
    ],
  },
  {
    code: "03",
    title: "QA / QC",
    body: "An independent reviewer validates closure precision, layering, scale, annotation consistency, and jurisdiction checklist items — before anything reaches your inbox.",
    bullets: [
      "Closure report validated to jurisdiction precision",
      "Layer, symbol, and text standards audit",
      "Jurisdiction checklist: signatures, notes, dedications",
    ],
  },
  {
    code: "04",
    title: "Delivery & Revisions",
    body: "You receive .dwg + PDF + supporting files. Revision rounds are included in scope, and you work directly with your drafter — no ticket system, no handoffs.",
    bullets: [
      "Receive .dwg, PDF, and supporting files",
      "Included revision rounds (typically 2)",
      "Direct drafter contact — no ticket queues",
    ],
  },
];

export default function Process() {
  return (
    <div data-testid="process-page">
      <section className="relative bg-zinc-50 border-b border-zinc-200 overflow-hidden ruler-top">
        <div className="absolute inset-0 bg-blueprint-grid pointer-events-none" aria-hidden="true" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-10 pt-20 md:pt-28 pb-14 md:pb-20">
          <div className="text-[11px] font-mono uppercase tracking-[0.3em] text-[#0044FF] mb-4">
            Process · How we work
          </div>
          <h1 className="font-display text-5xl md:text-7xl tracking-tighter font-medium text-zinc-950 max-w-4xl leading-[0.95]">
            Four steps from field data to sealed-ready drawing.
          </h1>
          <p className="mt-6 text-lg text-zinc-600 max-w-2xl leading-relaxed">
            Predictable, transparent, and built for firms who take their drawings seriously.
          </p>
        </div>
      </section>

      <section className="bg-white">
        {STEPS.map((s, i) => (
          <div
            key={s.code}
            className={`border-b border-zinc-200 ${i % 2 === 1 ? "bg-zinc-50" : "bg-white"}`}
            data-testid={`process-step-${s.code}`}
          >
            <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28 grid lg:grid-cols-12 gap-12 items-start">
              <div className="lg:col-span-5">
                <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-zinc-500">
                  Step
                </div>
                <div className="font-display text-[120px] md:text-[180px] leading-none text-zinc-950 tracking-tighter">
                  {s.code}
                </div>
                <h2 className="font-display text-3xl md:text-4xl tracking-tight text-zinc-950 mt-4">
                  {s.title}
                </h2>
              </div>
              <div className="lg:col-span-7">
                <p className="text-lg text-zinc-600 leading-relaxed">{s.body}</p>
                <ul className="mt-8 space-y-3 border-t border-zinc-200 pt-6">
                  {s.bullets.map((b) => (
                    <li key={b} className="flex items-start gap-3">
                      <span className="w-1 h-1 bg-[#0044FF] mt-3" />
                      <span className="text-zinc-800">{b}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        ))}
      </section>

      <section className="bg-[#0044FF] text-white py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid md:grid-cols-2 gap-10 items-center">
          <h2 className="font-display text-4xl md:text-5xl tracking-tight leading-[1.05]">
            Run this process on your next drawing.
          </h2>
          <div className="flex flex-wrap gap-3 md:justify-end">
            <Link
              to="/free-trial"
              className="px-6 py-4 bg-white text-[#0044FF] font-medium btn-lift"
              data-testid="process-cta-free-trial"
            >
              Start Free Trial
            </Link>
            <Link
              to="/contact"
              className="px-6 py-4 border border-white text-white font-medium btn-lift hover:bg-white/10 inline-flex items-center gap-2"
              data-testid="process-cta-contact"
            >
              Talk to us <ArrowRight size={14} weight="bold" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
